/**
 * Danish crew and doors for the home page of bidmio.dk.
 *
 * The split follows Danmarks Statistik (KBB03, October 2026), not the Slovak
 * "month to month" story: "survive" is too few jobs or too low a price, "grow"
 * is more jobs than people. The figure art is shared; only the labels change
 * (tiler → Anlægsgartner, facade → Kloakmester, developer → Hovedentreprenør).
 * Every answer line names only what bidmio.dk already claims: quotes with
 * labour and materials on separate lines, aconto invoices, FI-kort and
 * MobilePay on the invoice, unpaid invoices by customer and age with reminders,
 * day plan on the phone, leave and absence in the plan, materials and hours on
 * the job, equipment and machines with the site they are on, service agreements
 * and service reports, contribution margin per job, budget against actual cost.
 * No cash-flow forecast: the 13-week forecast has no screen yet on bidmio.dk.
 */
import { type CrewEntry, type CrewKey, type World, type WorldPage } from "../crewData";
import type { WorldsContent } from "../worldsContent";

export const DA_WORLD_PAGES: Record<World, WorldPage> = {
  survive: {
    href: "/naar-opgaverne-mangler/",
    door: "Det er mig: for få opgaver eller for lav pris",
    title: "Der er plads i kalenderen, og prisen bliver presset.",
  },
  grow: {
    href: "/naar-overblikket-mangler/",
    door: "Det er mig: flere opgaver end folk",
    title: "Opgaverne er der. Folkene og overblikket mangler.",
  },
};

export const DA_CREW: Record<CrewKey, CrewEntry> = {
  mason: {
    label: "Murer",
    home: "survive",
    survive: { quote: "Kunden spørger, om det kan laves uden regning.", answer: "Send et tilbud med arbejdsløn og materialer hver for sig. Det skal kunden bruge, hvis arbejdet giver fradrag." },
    grow: { quote: "Tre pladser i gang, og jeg ved ikke, hvor folkene er.", answer: "Dagens plan på hver mands telefon. Ingen ringer kl. halv syv." },
  },
  carpenter: {
    label: "Tømrer",
    home: "grow",
    survive: { quote: "Jeg har lagt ud for træet. Kunden har ikke betalt.", answer: "Send en acontofaktura, før I går i gang. Så er det ikke dig, der betaler materialerne." },
    grow: { quote: "Ordrebogen er fuld til sommer. Jeg mangler folk.", answer: "Sjak, sager og datoer ét sted. Du ser, hvor der er luft." },
  },
  electrician: {
    label: "Elektriker",
    home: "grow",
    survive: { quote: "Ham uden CVR-nummer var billigere.", answer: "Et ordentligt tilbud samme dag. Kunden, der vil have papir på arbejdet, har noget at vælge dig på." },
    grow: { quote: "Hvor blev de 40 meter kabel af?", answer: "Materialer skrevet på sagen. Du ser, hvad der mangler." },
  },
  plumber: {
    label: "VVS'er",
    home: "grow",
    survive: { quote: "Akutudkald i nat. Fakturaen kommer engang.", answer: "Skriv udkaldet på telefonen i nat. I morgen er det en faktura." },
    grow: { quote: "Montage, service og vagt. Det hele ligger i hovedet.", answer: "Montage og service i samme plan. Hovedet er frit." },
  },
  painter: {
    label: "Maler",
    home: "survive",
    survive: { quote: "Jeg har ventet to måneder på pengene.", answer: "Faktura med FI-kort og MobilePay, når I er færdige. Og et overblik over, hvem der skylder." },
    grow: { quote: "Sjakket maler, og jeg ved ikke hvor.", answer: "Hver mand ser sin sag. Du ser dem alle." },
  },
  roofer: {
    label: "Tagdækker",
    home: "grow",
    survive: { quote: "Det regner. Sjakket står, og lønnen løber.", answer: "Flyt sjakket til en anden sag i planen. Alle ser ændringen på telefonen." },
    grow: { quote: "To er syge, og taget står åbent.", answer: "Ferie og fravær i planen. Du ser, hvem der kan flyttes." },
  },
  service: {
    label: "Servicetekniker",
    home: "survive",
    survive: { quote: "Der er ingen kørsler i kalenderen i næste uge.", answer: "Kunder og serviceaftaler ét sted. Du ved, hvem der skal have eftersyn." },
    grow: { quote: "Seks teknikere, og jeg er blevet disponent.", answer: "Dagens plan, servicerapport og faktura fra telefonen." },
  },
  siteManager: {
    label: "Byggeleder",
    home: "grow",
    survive: { quote: "Bygherren betaler først om 60 dage.", answer: "Ubetalte fakturaer fordelt på kunde og alder. Du ser, hvad der hænger, og hvor længe." },
    grow: { quote: "Omsætningen stiger. Pengene gør ikke.", answer: "Dækningsbidrag på hver sag, ikke kun omsætning." },
  },
  owner: {
    label: "Mester",
    home: "grow",
    survive: { quote: "Lønnen skal ud fredag. Betalingen er ikke kommet.", answer: "Hvem skylder hvad, med rykkere. Du ved, hvem du ringer til først." },
    grow: { quote: "Tredive mand, og jeg ved ikke, hvilken sag der tjener penge.", answer: "Dækningsbidrag pr. sag, og hvem der er hvor i dag. Ét sted." },
  },
  tiler: {
    label: "Anlægsgartner",
    home: "survive",
    survive: { quote: "Kunden ville betale kontant.", answer: "FI-kort og MobilePay på fakturaen gør det let at betale rigtigt. Og kunden har papir på arbejdet." },
    grow: { quote: "Fem haver i gang, og grejet er altid det forkerte sted.", answer: "Udstyr med navn og nummer. Du ser, hvilken plads det står på." },
  },
  joiner: {
    label: "Snedker",
    home: "grow",
    survive: { quote: "Køkkenet er monteret. Restbeløbet er ikke betalt.", answer: "Aconto ved bestilling, resten på fakturaen ved montering." },
    grow: { quote: "Værksted og montage, og det er mig, der holder styr på det hele.", answer: "Timer i værkstedet og på montage på hver ordre." },
  },
  facade: {
    label: "Kloakmester",
    home: "survive",
    survive: { quote: "Jeg tog den for billigt. Leasingen venter ikke.", answer: "Se først, hvad kunderne skylder dig, før du siger ja til en for billig opgave." },
    grow: { quote: "Hvem har minigraveren i dag?", answer: "Maskiner med navn og nummer. Du ser, hvilken plads de står på." },
  },
  digger: {
    label: "Maskinfører",
    home: "grow",
    survive: { quote: "Maskinen stod stille hele ugen.", answer: "Se tomme uger i planen i god tid. Du kan nå at ringe til kunderne." },
    grow: { quote: "Ingen skrev maskintimerne.", answer: "Timer skrevet på sagen fra telefonen. Intet falder ud af fakturaen." },
  },
  developer: {
    label: "Hovedentreprenør",
    home: "grow",
    grow: { quote: "Tre projekter, ti underentreprenører. Budgettet skrider.", answer: "Budget, ændringer og faktiske omkostninger på hvert projekt. Du ser afvigelsen undervejs, ikke ved afleveringen." },
  },
};

/** Long Danish labels sit between short ones, so none is cut off at 1280 px. */
export const DA_CREW_ORDER: readonly CrewKey[] = [
  "mason", "facade", "carpenter", "service", "plumber", "electrician", "roofer",
  "siteManager", "developer", "owner", "digger", "painter", "tiler", "joiner",
];

export const DA_WORLDS: WorldsContent = {
  lang: "da",
  crew: DA_CREW,
  order: DA_CREW_ORDER,
  pages: DA_WORLD_PAGES,
  doors: {
    survive: {
      quotes: [
        "Der er ingen kørsler i kalenderen i næste uge.",
        "Ham uden CVR-nummer var billigere.",
        "Jeg tog den for billigt. Leasingen venter ikke.",
        "Jeg har ventet to måneder på pengene.",
      ],
      points: [
        "Kalenderen har huller, og tilbuddene bliver presset på prisen.",
        "Du har tjent pengene, men de er ikke kommet ind endnu.",
      ],
    },
    grow: {
      quotes: [
        "Ordrebogen er fuld til sommer. Jeg mangler folk.",
        "To er syge, og taget står åbent.",
        "Hvor blev de 40 meter kabel af?",
        "Omsætningen stiger. Pengene gør ikke.",
      ],
      points: [
        "Der er nok at lave. Det er folkene og overblikket, der mangler.",
        "Med hver ny ansat følger nye regler, ferie og fravær.",
      ],
    },
  },
  ui: {
    heading: "Hvem af dem er du?",
    subheading: "Klik på den, der ligner dig mest, og se, hvor du skal hen.",
    lineupAria: "Vælg den, der ligner dig mest",
    doorsBadge: "Lad os være ærlige",
    doorsTitle: "Hvor er du i dag?",
    doorsIntro:
      "Nogle har for få opgaver, eller kun opgaver til en pris, der ikke holder. Andre har flere opgaver end folk. Begge dele er svært, bare på hver sin måde.",
    doorLabel: "Dør",
    doorCta: "Det er mig",
    doorsFooter: "Er du et sted midt imellem? Se begge.",
  },
};
